import React, { useEffect, useState } from 'react'
import { withTranslation } from 'react-i18next'
import * as UIcons from '@iconscout/react-unicons'
import useSWR from 'swr'
import StatCard from 'components/DetailWidgets/StatCard'
import usePermissions from 'hooks/usePermissions'
import useBalances from 'hooks/useBalances'
import { formatAmount } from 'Helper'

export default withTranslation()(function Cards({ t }) {
  const { data: stats, error } = useSWR('dashboard/overview')
  const { credit, isLoading: loadingBalances } = useBalances()
  const { isPermitted } = usePermissions(['view_total_revenue', 'view_total_transactions'])

  const [highestRate, setHighestRate] = useState(0)

  useEffect(() => {
    const rates = stats?.data?.rewards?.rates
      ?.map((rate) => rate.rate)
      ?.filter((rate) => String(rate).includes('%'))
      ?.map((rate) => parseFloat(rate))

    if (rates?.length) setHighestRate(Math.max(...rates))
  }, [stats])

  // Distinguish "still fetching" from "loaded and genuinely zero" so the tiles
  // don't flash 0.00 on every visit.
  const loading = (!stats && !error) || loadingBalances

  const notPermitted = t('overview.not_permitted')

  const cards = [
    {
      key: 'revenue',
      icon: UIcons.UilMoneybag,
      tone: 'success',
      label: t('overview.revenue'),
      value: isPermitted?.view_total_revenue ? formatAmount(stats?.data?.total_revenue) : notPermitted
    },
    {
      key: 'credit',
      icon: UIcons.UilWallet,
      tone: 'primary',
      label: t('overview.credit'),
      value: formatAmount(credit?.balance ?? 0)
    },
    {
      key: 'transactions',
      icon: UIcons.UilReceipt,
      tone: 'blue',
      label: t('overview.total_transaction'),
      value: isPermitted?.view_total_transactions ? (stats?.data?.total_transactions ?? 0) : notPermitted
    },
    {
      key: 'redemption',
      icon: UIcons.UilGift,
      tone: 'gold',
      label: t('overview.redemption_balance'),
      value: formatAmount(stats?.data?.pending_settlement ?? 0)
    },
    {
      key: 'reward_rate',
      icon: UIcons.UilPercentage,
      tone: 'purple',
      label: t('overview.reward_rate'),
      value: `${Number(highestRate).toFixed(2)}%`
    }
  ]

  if (error) {
    return (
      <div className="w-full rounded-2xl border border-border-lines p-6 flex items-center gap-3">
        <UIcons.UilExclamationTriangle size={20} className="text-danger shrink-0" />
        <p className="text-sm text-secondary">{t('overview.stats_error')}</p>
      </div>
    )
  }

  return (
    <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
      {cards.map((card) => <StatCard key={card.key} loading={loading} {...card} />)}
    </div>
  )
})
